import { _isNil, _isNotEmpty, _notNil } from '@/littledash';
import {
  validateSampleTypeValue,
  validateSampleTypeLabel,
  validateDetailsOptions,
  getBasicSampleTypeErrorMessage,
  getBasicLabelErrorMessage,
  type ValidationData,
} from '@/utils/sampleTypeValidation';
import type { SampleType, SampleTypeRow } from '@/model/SampleType.model';

type ValidatorCallback = (valid: boolean) => void;

interface HotTableRefLike {
  hotInstance?: unknown;
}

interface BuildValidatorsParams {
  availableDetailOptions: Array<string>;
  validationData: ValidationData | null;
  sampleTypes: Array<SampleType>;
  hotTableRef: { current: HotTableRefLike | null };
}

const getSpreadsheetData = (hotTableRef: { current: HotTableRefLike | null }): Array<SampleTypeRow> => {
  const hotInstance = hotTableRef.current?.hotInstance;
  if (_notNil(hotInstance) && typeof (hotInstance as { getData?: unknown }).getData === 'function') {
    return (hotInstance as { getData: () => Array<SampleTypeRow> }).getData();
  }
  return [];
};

export const buildSampleTypeValidator = ({
  validationData,
  sampleTypes,
  hotTableRef,
}: Omit<BuildValidatorsParams, 'availableDetailOptions'>): ((
  this: { row: number },
  value: unknown,
  callback: ValidatorCallback
) => void) => {
  return function (this: { row: number }, value: unknown, callback: ValidatorCallback) {
    if (_isNil(value) || typeof value !== 'string') {
      callback(false);
      return;
    }

    const rowIndex = this.row;
    if (typeof rowIndex !== 'number') {
      callback(true);
      return;
    }

    if (_isNotEmpty(sampleTypes)) {
      const hasEditableSampleType = sampleTypes.some(
        (sampleType) => sampleType.system !== true && _isNil(sampleType.archived_at)
      );
      if (!hasEditableSampleType) {
        callback(false);
        return;
      }
    }

    const validation = validateSampleTypeValue(
      value,
      rowIndex,
      validationData,
      sampleTypes,
      getSpreadsheetData(hotTableRef)
    );
    callback(validation.isValid);
  };
};

export const buildSampleTypeErrorMessage = (validationData: ValidationData | null): ((value: unknown) => string) => {
  return (value: unknown) => getBasicSampleTypeErrorMessage(value as string, validationData);
};

export const buildLabelValidator = ({
  validationData,
  sampleTypes,
  hotTableRef,
}: Omit<BuildValidatorsParams, 'availableDetailOptions'>): ((
  this: { row: number },
  value: unknown,
  callback: ValidatorCallback
) => void) => {
  return function (this: { row: number }, value: unknown, callback: ValidatorCallback) {
    if (_isNil(value)) {
      callback(true);
      return;
    }

    if (typeof value !== 'string') {
      callback(false);
      return;
    }

    const rowIndex = this.row;
    if (typeof rowIndex !== 'number') {
      callback(true);
      return;
    }

    const validation = validateSampleTypeLabel(
      value,
      rowIndex,
      validationData,
      sampleTypes,
      getSpreadsheetData(hotTableRef)
    );
    callback(validation.isValid);
  };
};

export const buildLabelErrorMessage = (validationData: ValidationData | null): ((value: unknown) => string) => {
  return (value: unknown) => getBasicLabelErrorMessage(value as string, validationData);
};

export const buildDetailsValidator = (
  availableDetailOptions: Array<string>
): ((value: unknown, callback: ValidatorCallback) => void) => {
  return (value: unknown, callback: ValidatorCallback) => {
    callback(validateDetailsOptions(value, availableDetailOptions));
  };
};

interface BuildCellsCallbackParams {
  sampleTypes: Array<SampleType>;
  validationData: ValidationData | null;
}

export const buildCellsCallback = ({
  sampleTypes,
  validationData,
}: BuildCellsCallbackParams): ((row: number, col: number) => Record<string, unknown>) => {
  return function (row: number, col: number): Record<string, unknown> {
    const cellProperties: Record<string, unknown> = {};

    if (_notNil(sampleTypes[row])) {
      const isSystem = sampleTypes[row].system === true;
      const isArchived = _notNil(sampleTypes[row].archived_at);

      if (isSystem || isArchived) {
        cellProperties.readOnly = true;
        cellProperties.className = 'htMiddle readOnly';
      } else if (_notNil(validationData)) {
        const isInUse = validationData.in_use_team_glossary_api_ids.includes(sampleTypes[row].api_id);
        if (col === 0 && isInUse) {
          cellProperties.readOnly = true;
          cellProperties.className = 'htMiddle readOnly';
        }
      }
    }

    return cellProperties;
  };
};

export const buildAfterRendererCallback = (
  sampleTypes: Array<SampleType>
): ((
  td: HTMLElement,
  row: number,
  col: number,
  prop: string | number,
  value: unknown,
  cellProperties: { readOnly?: boolean }
) => void) => {
  return function (
    td: HTMLElement,
    row: number,
    _col: number,
    _prop: string | number,
    _value: unknown,
    cellProperties: { readOnly?: boolean }
  ): void {
    if (cellProperties.readOnly === true) {
      const sampleType = sampleTypes[row];
      if (_isNil(sampleType)) {
        return;
      }

      const isSystem = sampleType.system === true;
      const isArchived = _notNil(sampleType.archived_at);

      if (isSystem) {
        td.setAttribute('data-tooltip-id', 'global-tooltip-id');
        td.setAttribute('data-tooltip-content', 'This is a system sample type and cannot be modified');
        td.setAttribute('data-type', 'info');
      } else if (isArchived) {
        td.setAttribute('data-tooltip-id', 'global-tooltip-id');
        td.setAttribute('data-tooltip-content', 'This sample type is archived and cannot be modified');
        td.setAttribute('data-type', 'info');
      } else {
        td.setAttribute('data-tooltip-id', 'global-tooltip-id');
        td.setAttribute('data-tooltip-content', 'This sample type is in use and cannot be modified');
        td.setAttribute('data-type', 'info');
      }
    }
  };
};
